import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { Icon } from "./icons";
import { SectionHead } from "./SectionHead";
import { toast } from "./toast";

/**
 * The nine lines Audible asks for when a book is submitted.
 *
 * Six of them the project already knows and three it cannot, so it counts the
 * six and remembers the three. The counted ones are the point: total runtime,
 * how many files, how much they weigh are exactly what a person gets wrong at
 * the end of a long job, and they are also the ones nobody can check by eye.
 *
 * Shown as the form's own lines, in the form's own order and wording, so what
 * is copied and what is read are the same thing.
 */

interface Summary {
  title: string;
  title_default: string;
  title_is_custom: boolean;
  author: string;
  /** True only when the value shown was lifted out of the manuscript. A name
   *  somebody typed must not be labelled as read from a file. */
  author_from_manuscript: boolean;
  narrators: string[];
  product_id: string;
  minutes: number;
  hours: number;
  file_count: number;
  size_display: string;
  ftp_folder: string;
  samples: string[];
  missing_runtime: string[];
  missing_size: string[];
  incomplete: string[];
  text: string;
}

export function SubmissionSummary({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const { data: s } = useQuery({
    queryKey: ["submission", projectId],
    queryFn: () => api<Summary>(`/api/projects/${projectId}/submission`),
    // The runtimes and sizes it counts are filled in behind the request, so an
    // early open catches up rather than sitting on a wrong total.
    refetchInterval: (q) => {
      const d = q.state.data as Summary | undefined;
      return d && (d.missing_runtime.length || d.missing_size.length) ? 3000 : false;
    },
  });

  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api<Summary>(`/api/projects/${projectId}/submission`, { method: "PATCH", body: JSON.stringify(patch) }),
    onSuccess: (next) => qc.setQueryData(["submission", projectId], next),
    onError: (e: any) => toast(e?.message || "Couldn't save that.", "error"),
  });

  if (!s) return null;
  const waiting = s.missing_runtime.length + s.missing_size.length;

  return (
    <>
      <SectionHead title="Audible submission"
        sub="Everything the submission form asks for, counted from this project." />
      <div className="card sm-card">
        <div className="sm-lines">
          <Line label="Title">
            <Editable value={s.title} placeholder={s.title_default}
              onSave={(v) => save.mutate({ submission_title: v })} />
            {!s.title_is_custom && <span className="sm-from">from the project name</span>}
          </Line>

          <Line label="Author">
            <Editable value={s.author} placeholder="Who wrote it"
              onSave={(v) => save.mutate({ author: v })} />
            {s.author_from_manuscript && <span className="sm-from">read from the manuscript — check it</span>}
          </Line>

          <Line label="Narrator">
            <Names names={s.narrators} onChange={(names) => save.mutate({ narrators: names })} />
          </Line>

          <Line label="Product ID">
            {s.product_id
              ? <code className="sm-val">{s.product_id}</code>
              : <span className="sm-empty">Set it in Essentials above</span>}
          </Line>

          <Line label="Total run time in minutes (not including sample)">
            <code className="sm-val">{s.minutes}</code>
          </Line>
          <Line label="Total run time in hours (not including sample)">
            <code className="sm-val">{s.hours.toFixed(2)}</code>
          </Line>
          <Line label="Number of files (audio only and not including sample)">
            <code className="sm-val">{s.file_count}</code>
          </Line>
          <Line label="Total file size (audio only and including sample)">
            <code className="sm-val">{s.size_display}</code>
          </Line>
          <Line label="FTP folder">
            <code className="sm-val">{s.ftp_folder || <span className="sm-empty">Needs a title and a Product ID</span>}</code>
          </Line>
        </div>

        <div className="sm-foot">
          <span className="sm-notes">
            {/* Which file was left out of the counts, named rather than assumed:
                the whole asymmetry of this form turns on it. */}
            {s.samples.length > 0 ? (
              <>Treating <b>{s.samples.join(", ")}</b> as the retail sample — out of the runtime
                and the file count, in the total size.</>
            ) : (
              <>No file here has “sample” in its name, so every file is counted as part of the book.</>
            )}
            {waiting > 0 && (
              <em className="sm-waiting">
                {" "}Still measuring {waiting} file{waiting === 1 ? "" : "s"} — the totals will settle in a moment.
              </em>
            )}
            {s.incomplete.length > 0 && (
              <em className="sm-missing"> {s.incomplete.join(", ")} still to fill in.</em>
            )}
          </span>
          <button className="btn sm primary" onClick={() => copy(s.text)}>
            <Icon name="copy" size={14} /> Copy summary
          </button>
        </div>
      </div>
    </>
  );
}

function copy(text: string) {
  navigator.clipboard.writeText(text).then(
    () => toast("Submission summary copied.", "success"),
    () => toast("Couldn't reach the clipboard — select the lines and copy.", "error"),
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="sm-line">
      <span className="sm-label">{label}</span>
      <span className="sm-field">{children}</span>
    </div>
  );
}

/** Click to edit, Enter or blur to save, Escape to put it back. */
function Editable({ value, placeholder, onSave }: {
  value: string; placeholder: string; onSave: (v: string) => void;
}) {
  const [draft, setDraft] = useState(value);
  const [editing, setEditing] = useState(false);
  useEffect(() => { if (!editing) setDraft(value); }, [value, editing]);

  if (!editing) {
    return (
      <button className={`sm-edit${value ? "" : " empty"}`} onClick={() => setEditing(true)}
        title="Click to edit">
        {value || placeholder}
      </button>
    );
  }
  return (
    <input className="sm-input" autoFocus value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter") { setEditing(false); if (draft !== value) onSave(draft); }
        if (e.key === "Escape") { setDraft(value); setEditing(false); }
      }}
      onBlur={() => { setEditing(false); if (draft !== value) onSave(draft); }} />
  );
}

/** Several names, because most books have more than one narrator. */
function Names({ names, onChange }: { names: string[]; onChange: (n: string[]) => void }) {
  const [draft, setDraft] = useState("");
  const ref = useRef<HTMLInputElement>(null);

  const add = (raw: string) => {
    // A pasted "A, B" is two names, not one — splitting here means a paste from
    // an email does the obvious thing.
    const fresh = raw.split(",").map((n) => n.trim()).filter(Boolean)
      .filter((n) => !names.some((e) => e.toLowerCase() === n.toLowerCase()));
    setDraft("");
    if (fresh.length) onChange([...names, ...fresh]);
  };

  return (
    <span className="sm-names" onClick={() => ref.current?.focus()}>
      {names.map((n) => (
        <span key={n} className="sm-name">
          {n}
          <button type="button" aria-label={`Remove ${n}`} title={`Remove ${n}`}
            onClick={(e) => { e.stopPropagation(); onChange(names.filter((x) => x !== n)); }}>
            <Icon name="x" size={11} />
          </button>
        </span>
      ))}
      <input ref={ref} className="sm-name-input" value={draft}
        placeholder={names.length ? "Add another…" : "Who narrated it"}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(draft); }
          if (e.key === "Backspace" && !draft && names.length) onChange(names.slice(0, -1));
        }}
        onBlur={() => add(draft)} />
    </span>
  );
}
